import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { FilterContext, FilterEditorRoot } from "../types/filterEditor";
import { FilterNode } from "../types/filterTree";
import { countRules, FilterEditorAction, filterEditorReducer } from "../utils/filterEditorReducer";
import { FilterEditorError, validateFilterEditor } from "../utils/filterEditorSchema";
import { parseFilterTree, ParsedFilter, serializeFilterTree } from "../utils/filterTreeMapper";

interface EditorState {
    root: FilterEditorRoot;
    unsupported: boolean;
    /** Serialized tree the current `root` is compared against to know if there are unsaved edits. */
    baselineJson: string;
    /** `savedKey` this state was last loaded from — lets the "take over new data" effect below tell a
     * genuinely new `savedFilter` apart from a re-render with the same one. */
    loadedKey: string;
}

type InternalAction =
    | FilterEditorAction
    | { type: "load"; savedKey: string; parsed: ParsedFilter }
    | { type: "clearUnsupported" }
    | { type: "markSaved"; savedTree: FilterNode | null }
    | { type: "applyBlueprint"; root: FilterEditorRoot };

const baselineOf = (parsed: ParsedFilter, savedKey: string): string =>
    parsed.supported ? JSON.stringify(serializeFilterTree(parsed.root)) : savedKey;

const buildLoadedState = (savedKey: string, parsed: ParsedFilter): EditorState => ({
    root: parsed.root,
    unsupported: !parsed.supported,
    baselineJson: baselineOf(parsed, savedKey),
    loadedKey: savedKey,
});

const editorStateReducer = (state: EditorState, action: InternalAction): EditorState => {
    if (action.type === "load") {
        return buildLoadedState(action.savedKey, action.parsed);
    }
    if (action.type === "clearUnsupported") {
        // Keeps the *old* baseline (the unsupported filter's raw JSON) and `loadedKey` unchanged, so this
        // correctly shows up as an unsaved edit (`isDirty`) instead of looking like a no-op.
        return { ...state, root: [], unsupported: false };
    }
    if (action.type === "applyBlueprint") {
        // An edit like any other: the baseline stays, so it counts as unsaved unless it equals what is saved.
        // Also lifts the lock of an unsupported filter, the blueprint is how it gets fixed by hand.
        return { ...state, root: action.root, unsupported: false };
    }
    if (action.type === "markSaved") {
        // The baseline is the tree that was actually *sent*, not the current draft: edits made while the save was
        // pending stay unsaved changes instead of being adopted (and then replaced by the refetch).
        // `loadedKey` stays as is: it is the serialized *incoming* `savedFilter`, so the refetch that follows a
        // save is still recognised as new data and taken over.
        return state.unsupported ? state : { ...state, baselineJson: JSON.stringify(action.savedTree) };
    }
    return { ...state, root: filterEditorReducer(state.root, action) };
};

/**
 * Local draft of a BE filter (`cascade.filter` / `offer.filter`). Nothing is persisted here,
 * the caller saves `toTree()` and then calls the mutation.
 */
export const useFilterEditor = (savedFilter: unknown, context: FilterContext, fieldTags?: ReadonlySet<string>) => {
    const savedKey = JSON.stringify(savedFilter ?? null);

    // Lazy init runs once, at mount — the effect below handles every `savedFilter` change after that.
    const [state, dispatchState] = useReducer(editorStateReducer, savedKey, (key) =>
        buildLoadedState(key, parseFilterTree(savedFilter))
    );
    const [showErrors, setShowErrors] = useState(false);

    const currentJson = state.unsupported ? state.loadedKey : JSON.stringify(serializeFilterTree(state.root));
    const isDirty = currentJson !== state.baselineJson;

    // Take over a `savedFilter` that changed since the last load (switched to a different cascade/offer, or
    // reloaded after a save elsewhere) unless there are unsaved edits. Compared against `state.loadedKey`
    // rather than recomputing the baseline from the *new* `savedFilter` on every render — that would compare
    // the incoming baseline against the still-old `root` and false-positive as "dirty" on every switch,
    // permanently refusing to load anything past the first cascade/offer opened in this session.
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;
    useEffect(() => {
        if (savedKey === state.loadedKey || isDirtyRef.current) {
            return;
        }
        dispatchState({ type: "load", savedKey, parsed: parseFilterTree(savedFilter) });
        setShowErrors(false);
    }, [savedKey, state.loadedKey]);

    const dispatch = useCallback((action: FilterEditorAction) => dispatchState(action), []);

    const errors: FilterEditorError[] = useMemo(
        () => validateFilterEditor(state.root, context, fieldTags),
        [state.root, context, fieldTags]
    );

    const validate = useCallback((): boolean => {
        setShowErrors(true);
        return errors.length === 0;
    }, [errors]);

    const toTree = useCallback((): FilterNode | null => serializeFilterTree(state.root), [state.root]);

    const clearUnsupported = useCallback(() => {
        dispatchState({ type: "clearUnsupported" });
    }, []);

    /** Call after a successful save with the tree that was sent, so that state stops counting as unsaved. */
    const markSaved = useCallback(
        (savedTree: FilterNode | null) => dispatchState({ type: "markSaved", savedTree }),
        []
    );

    /** Replaces the whole draft, e.g. with a filter pasted into the blueprint. Stays an unsaved change. */
    const applyBlueprint = useCallback((root: FilterEditorRoot) => {
        dispatchState({ type: "applyBlueprint", root });
        setShowErrors(false);
    }, []);

    const reset = useCallback((next: FilterEditorRoot) => {
        dispatchState({ type: "replace", root: next });
        setShowErrors(false);
    }, []);

    return {
        root: state.root,
        dispatch,
        context,
        unsupported: state.unsupported,
        clearUnsupported,
        isDirty,
        errors,
        showErrors,
        validate,
        toTree,
        reset,
        markSaved,
        applyBlueprint,
        fieldTags,
        /** Raw JSON BE has, only for a filter the editor cannot show (the blueprint lets the user fix it by hand) */
        unsupportedSavedJson: state.unsupported ? state.loadedKey : null,
        ruleCount: countRules(state.root),
    };
};

export type FilterEditorState = ReturnType<typeof useFilterEditor>;
